import { Injectable, inject } from '@angular/core';
import {HttpRequest, HttpEvent, HttpHandler, HttpResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {mergeMap, retryWhen, tap} from 'rxjs/operators';

import {SessionService} from '@core';
import {ErrorService} from '../services/error/error.service';

@Injectable()
export class HttpInterceptor implements HttpInterceptor {
    private readonly sessionService = inject(SessionService);
    private readonly errorService = inject(ErrorService);

    private readonly RetryMaxAttempts = 1;
    private readonly ExcludedUrls = ['/assets/lang'];

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.ExcludedUrls.some(url => request.url.includes(url))) {
            return next.handle(request);
        }

        let requestHeaders = request.headers
            .set('Content-Type', 'application/json')
            .set('api-version', this.sessionService.apiVersion)
            .set('If-Modified-Since', '0'); // fix for IE11, because it caches ajax requests;

        if (this.sessionService.apiKey && this.sessionService.userName) {
            requestHeaders = requestHeaders
                .set('x-api-key', this.sessionService.apiKey)
                .set('username', this.sessionService.userName);
        }

        if (this.sessionService.bearerToken) {
            let isSameOrigin: boolean;
            if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(request.url)) {
                isSameOrigin = true;
            } else {
                try {
                    isSameOrigin = new URL(request.url).origin === window.location.origin;
                } catch {
                    isSameOrigin = false;
                }
            }

            if (isSameOrigin) {
                requestHeaders = requestHeaders.set('Authorization', `Bearer ${this.sessionService.bearerToken}`);
            }
        }

        const secureRequest = request.clone({
            headers: requestHeaders
        });

        return next.handle(secureRequest).pipe(
            retryWhen(errors =>
                errors.pipe(
                    mergeMap((error, count) =>
                        error.status < 500 || count === this.RetryMaxAttempts ? throwError(error) : of(error)
                    )
                )
            ),
            tap(
                event => {
                    if (event instanceof HttpResponse) {
                        return event;
                    }
                    return null;
                },
                err => {
                    this.errorService.handleError(err);
                    return err;
                }
            )
        );
    }
}
